import React, { memo, useCallback } from "react";
import { Modal, Button } from "@repo/core-ui";
import { Loader2, Save, Globe, Shield, Activity, Settings } from "lucide-react";
import { ChannelInsert, ChannelUpdate } from "@repo/types";

interface ChannelProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  isEditMode: boolean;
  isSaving: boolean;
  handleSave: () => void;
  insertForm: Partial<ChannelInsert>;
  setInsertForm: React.Dispatch<React.SetStateAction<Partial<ChannelInsert>>>;
  updateForm: Partial<ChannelUpdate>;
  setUpdateForm: React.Dispatch<React.SetStateAction<Partial<ChannelUpdate>>>;
}

export const ChannelProfileModal: React.FC<ChannelProfileModalProps> = memo(
  ({
    isOpen,
    onClose,
    isEditMode,
    isSaving,
    handleSave,
    insertForm,
    setInsertForm,
    updateForm,
    setUpdateForm,
  }) => {
    const currentForm = isEditMode ? updateForm : insertForm;
    const setCurrentForm = isEditMode ? setUpdateForm : setInsertForm;

    const handleChange = useCallback(
      (
        e: React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >,
      ) => {
        const { name, value, type } = e.target;
        let finalValue: any = value;

        if (type === "number") finalValue = value === "" ? "" : Number(value);
        if (name === "IsActive" || name === "IsCredentialsEncrypted")
          finalValue = Number(value);

        setCurrentForm((prev: any) => ({ ...prev, [name]: finalValue }));
      },
      [setCurrentForm],
    );

    const handleFeatureToggle = useCallback(
      (feature: string, checked: boolean) => {
        setCurrentForm((prev: any) => {
          const currentFeatures = prev.SupportedFeatures || [];
          const nextFeatures = checked
            ? [...currentFeatures, feature]
            : currentFeatures.filter((f: any) => f !== feature);
          return { ...prev, SupportedFeatures: nextFeatures };
        });
      },
      [setCurrentForm],
    );

    const renderInput = useCallback(
      (
        label: string,
        name: string,
        type: string = "text",
        placeholder: string = "",
      ) => (
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-slate-500 dark:text-slate-500 uppercase tracking-wider ml-1">
            {label}
          </label>
          <input
            type={type}
            name={name}
            value={(currentForm as any)[name] || ""}
            onChange={handleChange}
            placeholder={placeholder}
            className="w-full px-4 py-3 bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-100 dark:border-white/10 rounded-xl text-sm font-bold text-slate-700 dark:text-slate-200 placeholder:text-slate-300 dark:placeholder:text-slate-600 focus:ring-2 focus:ring-blue-100 dark:focus:ring-white/5 focus:border-blue-200 dark:focus:border-white/20 transition-all outline-none"
          />
        </div>
      ),
      [currentForm, handleChange],
    );

    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        title={isEditMode ? "Edit Channel Settings" : "Register New Channel"}
        maxWidth="max-w-4xl"
        maxHeight="max-h-[95vh] sm:max-h-[90vh]"
        footer={
          <div className="flex justify-end items-center w-full gap-3 py-2 px-1">
            <Button
              variant="ghost"
              onClick={onClose}
              className="px-6 rounded-xl font-bold text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600 border-none px-8 rounded-xl flex items-center gap-2 shadow-sm"
              disabled={isSaving}
            >
              {isSaving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Save size={16} />
              )}
              {isSaving
                ? "Saving..."
                : isEditMode
                  ? "Update Channel"
                  : "Register Channel"}
            </Button>
          </div>
        }
      >
        <div className="space-y-8 py-4 px-2">
          {/* Section 1: Basic Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-100 dark:border-white/10">
              <Globe size={18} className="text-blue-600 dark:text-blue-400" />
              <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                Base Identity
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderInput(
                "Channel Code",
                "ChannelCode",
                "text",
                "e.g. AGODA, BOOKING",
              )}
              {renderInput(
                "Channel Name",
                "ChannelName",
                "text",
                "e.g. Agoda, Booking.com",
              )}
              {renderInput(
                "Channel Type",
                "ChannelType",
                "text",
                "e.g. OTA, B2B",
              )}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider ml-1">
                  Active Status
                </label>
                <select
                  name="IsActive"
                  value={currentForm.IsActive}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-blue-50/50 dark:bg-[#1b254b]/50 border border-blue-100 dark:border-white/10 rounded-xl text-sm font-black text-blue-700 dark:text-blue-400 outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900/20 transition-all appearance-none cursor-pointer"
                >
                  <option
                    value={1}
                    className="dark:bg-[#111c44] dark:text-slate-200"
                  >
                    ACTIVE
                  </option>
                  <option
                    value={0}
                    className="dark:bg-[#111c44] dark:text-slate-200"
                  >
                    INACTIVE
                  </option>
                </select>
              </div>
            </div>
          </div>

          {/* Section 2: Technical Configuration */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-100 dark:border-white/10">
              <Settings
                size={18}
                className="text-slate-600 dark:text-slate-400"
              />
              <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                Technical Config
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderInput(
                "API Base URL",
                "APIBaseURL",
                "text",
                "https://api.provider.com",
              )}
              {renderInput("API Version", "APIVersion", "text", "v1.0")}
              {renderInput("Protocol", "Protocol", "text", "REST / SOAP")}
              {renderInput("Auth Type", "AuthType", "text", "Bearer / API Key")}
            </div>
          </div>

          {/* Section 3: Commercials & Priority */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-100 dark:border-white/10">
              <Activity
                size={18}
                className="text-orange-600 dark:text-orange-400"
              />
              <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                Commercials & Priority
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {renderInput("Priority", "Priority", "number", "0")}
              {renderInput(
                "Commission %",
                "CommissionPercentage",
                "number",
                "0.00",
              )}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">
                  Commission Type
                </label>
                <select
                  name="CommissionType"
                  value={currentForm.CommissionType || ""}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-100 dark:border-white/10 rounded-xl text-sm font-bold text-slate-700 dark:text-slate-200 outline-none cursor-pointer"
                >
                  <option
                    value=""
                    className="dark:bg-[#111c44] dark:text-slate-400"
                  >
                    Select Type
                  </option>
                  <option
                    value="PERCENTAGE"
                    className="dark:bg-[#111c44] dark:text-slate-200"
                  >
                    Percentage
                  </option>
                  <option
                    value="FIXED"
                    className="dark:bg-[#111c44] dark:text-slate-200"
                  >
                    Fixed Amount
                  </option>
                </select>
              </div>
              {renderInput(
                "Currency Code",
                "CurrencyCode",
                "text",
                "USD / INR",
              )}
              {renderInput("Logo URL", "LogoURL", "text", "Public URL to logo")}
            </div>
          </div>

          {/* Section 4: Security & Support */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-100 dark:border-white/10">
              <Shield
                size={18}
                className="text-emerald-600 dark:text-emerald-400"
              />
              <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                Security & Support
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-500 dark:text-slate-500 uppercase tracking-wider ml-1">
                  Encrypt Credentials
                </label>
                <select
                  name="IsCredentialsEncrypted"
                  value={currentForm.IsCredentialsEncrypted}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-xl text-sm font-black transition-all outline-none cursor-pointer ${
                    currentForm.IsCredentialsEncrypted === 1
                      ? "bg-green-50 border-green-100 text-green-700 dark:bg-green-900/20 dark:border-green-900/40 dark:text-green-400"
                      : "bg-amber-50 border-amber-100 text-amber-700 dark:bg-amber-900/20 dark:border-amber-900/40 dark:text-amber-400"
                  }`}
                >
                  <option
                    value={1}
                    className="dark:bg-[#111c44] dark:text-slate-200"
                  >
                    YES - ENCRYPTED
                  </option>
                  <option
                    value={0}
                    className="dark:bg-[#111c44] dark:text-slate-200"
                  >
                    NO - PLAIN TEXT
                  </option>
                </select>
              </div>
              {renderInput(
                "Support Email",
                "SupportEmail",
                "email",
                "support@provider.com",
              )}
              <div className="md:col-span-2">
                {renderInput(
                  "Documentation URL",
                  "DocumentationURL",
                  "text",
                  "Link to API docs",
                )}
              </div>
            </div>
          </div>

          {/* Section 5: Supported Features */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-100 dark:border-white/10">
              <Activity
                size={18}
                className="text-blue-600 dark:text-blue-400"
              />
              <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                Supported Features
              </h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {(
                ["RATES", "INVENTORY", "BOOKINGS", "RESTRICTIONS"] as const
              ).map((feature) => {
                const isChecked = (
                  currentForm.SupportedFeatures || []
                ).includes(feature);
                return (
                  <label
                    key={feature}
                    className={`flex items-center gap-3 p-4 rounded-xl border transition-all cursor-pointer ${
                      isChecked
                        ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-900/40 text-blue-700 dark:text-blue-400 shadow-sm"
                        : "bg-slate-50 dark:bg-[#1b254b]/50 border-slate-100 dark:border-white/10 text-slate-400 dark:text-slate-500 hover:border-slate-200 dark:hover:border-white/20"
                    }`}
                  >
                    <div className="relative flex items-center">
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={isChecked}
                        onChange={(e) =>
                          handleFeatureToggle(feature, e.target.checked)
                        }
                      />
                      <div
                        className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-all ${
                          isChecked
                            ? "bg-blue-600 dark:bg-blue-500 border-blue-600 dark:border-blue-500 shadow-lg shadow-blue-500/30"
                            : "bg-white dark:bg-[#1b254b] border-slate-300 dark:border-white/10"
                        }`}
                      >
                        {isChecked && (
                          <div className="w-1.5 h-1.5 bg-white rounded-full animate-in zoom-in duration-200" />
                        )}
                      </div>
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider">
                      {feature}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-500 dark:text-slate-500 uppercase tracking-wider ml-1">
              Notes
            </label>
            <textarea
              name="Notes"
              value={currentForm.Notes || ""}
              onChange={handleChange}
              rows={3}
              placeholder="Internal notes about this channel..."
              className="w-full px-4 py-3 bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-100 dark:border-white/10 rounded-xl text-sm font-medium text-slate-700 dark:text-slate-200 placeholder:text-slate-300 dark:placeholder:text-slate-600 focus:ring-2 focus:ring-blue-100 dark:focus:ring-white/5 transition-all outline-none resize-none"
            />
          </div>
        </div>
      </Modal>
    );
  },
);
ChannelProfileModal.displayName = "ChannelProfileModal";
